import * as React from 'react';
import {cn} from '../lib/utils';

export type SuggestionsProps = React.HTMLAttributes<HTMLDivElement>;

export const Suggestions = React.forwardRef<HTMLDivElement, SuggestionsProps>(
  ({className, ...props}, ref) => (
    <div
      ref={ref}
      className={cn('flex w-full flex-col items-center gap-2', className)}
      {...props}
    />
  ),
);
Suggestions.displayName = 'Suggestions';

export type SuggestionsLabelProps = React.HTMLAttributes<HTMLSpanElement>;

export const SuggestionsLabel = React.forwardRef<
  HTMLSpanElement,
  SuggestionsLabelProps
>(({className, children = 'Suggestions:', ...props}, ref) => (
  <span
    ref={ref}
    className={cn('text-sm font-medium text-muted-foreground', className)}
    {...props}
  >
    {children}
  </span>
));
SuggestionsLabel.displayName = 'SuggestionsLabel';

export type SuggestionsListProps = React.HTMLAttributes<HTMLDivElement>;

export const SuggestionsList = React.forwardRef<
  HTMLDivElement,
  SuggestionsListProps
>(({className, ...props}, ref) => (
  <div
    ref={ref}
    className={cn('flex w-full flex-col gap-2', className)}
    {...props}
  />
));
SuggestionsList.displayName = 'SuggestionsList';

export type SuggestionProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick'
> & {
  suggestion: string;
  onClick?: (suggestion: string) => void;
};

export const Suggestion = React.forwardRef<HTMLButtonElement, SuggestionProps>(
  ({className, suggestion, onClick, children, ...props}, ref) => {
    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.(suggestion);
      },
      [onClick, suggestion],
    );

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        className={cn(
          'rounded-lg border border-border bg-accent px-4 py-3 text-left text-sm text-foreground',
          'transition-colors hover:bg-accent-foreground/5',
          className,
        )}
        {...props}
      >
        {children ?? suggestion}
      </button>
    );
  },
);
Suggestion.displayName = 'Suggestion';
